/** A funnel: stages a thing passes through, each narrower than the last (ADR-0212, P7): received,
 * read, to review, in the ledger.
 *
 * Bars are left-aligned (a shared edge is the easiest thing to compare), each with its count printed
 * at the right, and sized against the largest stage, so the shape is the claim: a stage that loses
 * most of what entered it looks like it. `value` is geometry only; the figure and the step
 * between two stages (`stepText`: "86% of received") are the caller's strings, because a funnel that
 * divided its own stages would be a second computation of a share. A stage that needs somebody uses
 * the amber (`colour: "warn"`) and says why in `note`; the colour is never the only carrier. A
 * stage with a `receipt` opens it when the caller passes `onOpenReceipt`. See `BarRows`.
 */

import { BarRows } from "./BarRows";
import type { BarRow } from "./BarRows";
import type { ChartColour } from "./chartTokens";
import type { OpenReceipt } from "./receipt";

export interface FunnelStage {
  key: string;
  label: string;
  /** Geometry only: the bar's width against the largest stage. */
  value: number;
  /** The count as printed. */
  text: string;
  colour?: ChartColour;
  /** Extra words for the tooltip: why this stage holds what it holds. */
  note?: string;
  /** Printed under the stage, before the next one: how it follows from the previous stage. */
  stepText?: string;
  receipt?: string;
}

const RAMP: readonly ChartColour[] = ["ramp1", "ramp2", "ramp3", "ramp4"];

export function Funnel({
  stages,
  caption,
  unit = "Count",
  onOpenReceipt,
  testId = "funnel",
}: {
  stages: readonly FunnelStage[];
  caption: string;
  /** The count column's heading in the text alternative. */
  unit?: string;
  onOpenReceipt?: OpenReceipt;
  testId?: string;
}) {
  const rows: BarRow[] = stages.map((stage, index) => ({
    key: stage.key,
    label: stage.label,
    value: stage.value,
    text: stage.text,
    colour: stage.colour ?? RAMP[Math.min(index, RAMP.length - 1)] ?? "ramp1",
    note: stage.note,
    between: index < stages.length - 1 ? stage.stepText : undefined,
    receipt: stage.receipt,
  }));
  return (
    <BarRows
      rows={rows}
      caption={caption}
      head={["Stage", unit]}
      testId={testId}
      onOpenReceipt={onOpenReceipt}
    />
  );
}
